import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Theme store — light / dark / system.
 *
 * `.impeccable.md` 기준:
 *   - Light = 논문 인쇄물 (warm paper)
 *   - Dark  = 천문대 콘솔 (deep ink)
 *
 * Step 6 (GDEV-50): `.dark` 클래스를 `document.documentElement` 에 토글하여
 * `variant-c.tokens.css` 의 `.dark :root` 토큰 스위치를 트리거한다.
 * `system` 모드는 `prefers-color-scheme` 미디어 쿼리로 결정.
 */

export type Theme = 'light' | 'dark' | 'system'

interface ThemeState {
  theme: Theme
  setTheme: (theme: Theme) => void
  toggle: () => void
}

function resolveEffectiveMode(theme: Theme): 'light' | 'dark' {
  if (theme === 'system') {
    if (typeof window === 'undefined') return 'light'
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return theme
}

function applyToDocument(theme: Theme): void {
  if (typeof document === 'undefined') return
  const effective = resolveEffectiveMode(theme)
  if (effective === 'dark') {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'system',
      setTheme: (theme) => {
        applyToDocument(theme)
        set({ theme })
      },
      toggle: () => {
        const current = get().theme
        const next: Theme = current === 'dark' ? 'light' : 'dark'
        applyToDocument(next)
        set({ theme: next })
      },
    }),
    {
      name: 'robotai-theme',
      onRehydrateStorage: () => (state) => {
        if (state) applyToDocument(state.theme)
      },
    },
  ),
)

/**
 * 앱 초기 로드 시점에 1 회 호출하여 current theme 을 document 에 적용.
 * `main.tsx` 에서 `initTheme()` 호출로 명시화 — 모듈 import 사이드이펙트 회피.
 * vitest/SSR 환경에서는 호출을 생략하여 테스트 격리성 확보.
 */
export function initTheme(): void {
  applyToDocument(useThemeStore.getState().theme)
}
